import { useToastStore } from 'store/toastStore';
import { addInitialSignupConfiguration, SignupCreditsRequest } from 'apis/adminServices/initialSignupServices';
import { Dispatch, memo, SetStateAction, useCallback, useState } from 'react';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import { AdminModal, AirlinePicker } from 'components/admin';

const formatDateToISO = (date: Date | null): string | '' => {
    if (!date) return '';
    return date.toISOString();
};

export interface InitialSignupFormConfig {
    configName: string; 
    configurationType: string;
    creditAmount: number;
    promoLimit: number; 
    flights: number[]; 
    startDate: string;
    endDate: string;
    isActive: boolean;
}

interface AddInitialSignupModalProps {
    newConfig: InitialSignupFormConfig;
    setNewConfig: Dispatch<SetStateAction<InitialSignupFormConfig>>; 
    onAdd: (e: React.FormEvent) => Promise<void>; 
    onClose: () => void;
    refreshTable?: () => void;
}

const AddInitialSignupModal: React.FC<AddInitialSignupModalProps> = memo(({ newConfig, setNewConfig, onAdd, onClose, refreshTable }) => {
    const [loading, setLoading] = useState(false);
    const { showToast } = useToastStore();
    const [creditError, setCreditError] = useState<string>('');

    const formData = newConfig;

    // Handler for DatePicker onChange
    const handleDateChange = useCallback((field: 'startDate' | 'endDate') => (date: Date | null) => {
        if (!date) {
            setNewConfig(prev => ({
                ...prev,
                [field]: '',
            }));
            return;
        }

        // Check if date is in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const selectedDate = new Date(date);
        selectedDate.setHours(0, 0, 0, 0);

        if (selectedDate.getTime() < today.getTime()) {
            showToast('Cannot select past dates.', 'error');
            return;
        }

        const formattedDate = formatDateToISO(date);

        setNewConfig(prev => ({
            ...prev,
            [field]: formattedDate,
        }));
        
        if (field === 'startDate' && date && formData.endDate && date.getTime() > new Date(formData.endDate).getTime()) {
             showToast('Start date cannot be after end date.', 'error');
        } else if (field === 'endDate' && date && formData.startDate && date.getTime() < new Date(formData.startDate).getTime()) {
             showToast('End date cannot be before start date.', 'error');
        }
    }, [setNewConfig, formData.endDate, formData.startDate, showToast]);

    // Handle airline selection change
    const handleAirlineChange = (airlineIds: number[]) => {
        setNewConfig(prev => ({ ...prev, flights: airlineIds }));
    };

    // Handle field changes
    const handleFieldChange = useCallback((e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
        const { id, value, type } = e.target;
        
        // Validation for credit amount
        if (id === 'creditAmount') {
            const numValue = parseInt(value, 10) || 0;
            if (numValue > 100) {
                setCreditError('Credit amount cannot exceed 100');
                return;
            }
            setCreditError('');
        }
        
        const isCheckbox = type === 'checkbox';
        const checked = isCheckbox && e.target instanceof HTMLInputElement ? e.target.checked : undefined;
        setNewConfig(prev => ({ 
            ...prev, 
            [id]: type === 'number'
                ? parseInt(value, 10) || 0
                : type === 'checkbox'
                    ? Boolean(checked)
                    : id === 'isActive'
                        ? value === 'true'
                        : value
        }));
    }, [setNewConfig]);

    const handleLocalSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validation
        if (!newConfig.configName.trim()) {
            showToast('Configuration Name is required.', 'error');
            return;
        }
        if (newConfig.creditAmount <= 0) {
            showToast('Credit Amount must be greater than 0.', 'error');
            return;
        }
        if (newConfig.creditAmount > 100) {
            showToast('Credit Amount cannot exceed 100.', 'error');
            return;
        }
        if (creditError) {
            showToast('Please fix validation errors before submitting.', 'error');
            return;
        }
        // if (!newConfig.flights || newConfig.flights.length === 0) {
        //     showToast('Please select at least one flight.', 'error');
        //     return;
        // }
        // Validate dates are not in the past
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        if (formData.startDate) {
            const startDate = new Date(formData.startDate);
            startDate.setHours(0, 0, 0, 0);
            if (startDate.getTime() < today.getTime()) {
                showToast('Valid From date cannot be in the past.', 'error');
                return;
            }
        }
        
        if (formData.endDate) {
            const endDate = new Date(formData.endDate);
            endDate.setHours(0, 0, 0, 0);
            if (endDate.getTime() < today.getTime()) {
                showToast('Valid To date cannot be in the past.', 'error');
                return;
            }
        }
        
        if (formData.startDate && formData.endDate && new Date(formData.startDate).getTime() > new Date(formData.endDate).getTime()) {
            showToast('Valid From date cannot be after Valid To date.', 'error');
            return;
        }

        setLoading(true);
        try {
            const apiConfig: SignupCreditsRequest = {
                CreditAmount: newConfig.creditAmount,
                PromoLimit: newConfig.promoLimit, 
                ConfigName: newConfig.configName, 
                ConfigurationType: newConfig.configurationType, 
                AirlineIds: newConfig.flights, 
                StartDate: newConfig.startDate,
                EndDate: newConfig.endDate,
                IsActive: newConfig.isActive,
            };
            
            const response = await addInitialSignupConfiguration(apiConfig);
            showToast(response.message || 'Configuration added successfully', 'success');
            
            // Reset form and close modal
            setNewConfig({ configName: '', configurationType: '', creditAmount: 0, promoLimit: 0, flights: [], startDate: '', endDate: '', isActive: true });
            refreshTable?.();
            onClose();
        } catch (error) {
            console.error('Submission failed:', error);
            showToast('Failed to add configuration. Please try again.', 'error'); 
        } finally {
            setLoading(false);
        }
    };

    return (
        <AdminModal
            isOpen={true}
            onClose={onClose}
            title="Add New Promo Configuration"
            size="xl"
        >
                    <form onSubmit={handleLocalSubmit}>
                             {/* Config Name */}
                            <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="configName">
                                    Configuration Name
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="configName"
                                    value={newConfig.configName}
                                    onChange={handleFieldChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    required
                        disabled={loading}
                                />
                            </div>

            <div className="mb-4">
                <label className="block text-xs text-gray-500 mb-2" htmlFor="isActive">
                    Status
                </label>
                <select
                    id="isActive"
                    value={String(newConfig.isActive)}
                    onChange={handleFieldChange}
                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                    disabled={loading}
                >
                    <option value="true">Active</option>
                    <option value="false">Disable</option>
                </select>
            </div>

                {/* Flight Selection - Using AirlinePicker Component */}
                <div className="mb-4">
                    <AirlinePicker
                        selectedAirlineIds={newConfig.flights || []}
                        onChange={handleAirlineChange}
                                    placeholder="Search airlines..."
                        label="Airlines"
                        showChips={true}
                        disabled={loading}
                        isRequired={false}
                    />
                            </div>

                             {/* Config Type */}
                             <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="configurationType">
                                    Display Name
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="text"
                                    id="configurationType"
                                    value={newConfig.configurationType}
                                    onChange={handleFieldChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    required
                        disabled={loading}
                                />
                            </div>

                            {/* Credit Amount */}
                            <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="creditAmount">
                                    Choose Number of Tokens
                                    <span className="text-red-500 ml-1">*</span>
                                </label>
                                <input
                                    type="number"
                                    id="creditAmount"
                                    value={newConfig.creditAmount}
                                    onChange={handleFieldChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                                    min="1"
                                    max="100"
                                    required
                        disabled={loading}
                                />
                                {creditError && <p className="text-red-500 text-xs mt-1">{creditError}</p>}
                            </div>

                            {/* Promo Limit */}
                            <div className="mb-4">
                                <label className="block text-xs text-gray-500 mb-2" htmlFor="promoLimit">
                                    Applicable User Limit
                                </label>
                                <input
                                    type="number"
                                    id="promoLimit"
                                    value={newConfig.promoLimit}
                                    onChange={handleFieldChange}
                                    className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none border-none"
                    min="0"
                        disabled={loading}
                                />
                            </div>

                {/* Date Pickers */}
                            <div className="mb-4 flex gap-4">
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 mb-2" htmlFor="startDate">Valid From</label>
                                    <DatePicker
                                        id="startDate"
                                        selected={formData.startDate ? new Date(formData.startDate) : null}
                                        onChange={handleDateChange('startDate')}
                                        dateFormat="yyyy-MM-dd"
                                        className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                                        placeholderText="YYYY-MM-DD"
                                        disabled={loading}
                                        minDate={new Date()}
                                        onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                                    />
                                </div>
                                <div className="flex-1">
                                    <label className="block text-xs text-gray-500 mb-2" htmlFor="endDate">Valid To</label>
                                    <DatePicker
                                        id="endDate"
                                        selected={formData.endDate ? new Date(formData.endDate) : null}
                                        onChange={handleDateChange('endDate')}
                                        dateFormat="yyyy-MM-dd"
                                        className="w-full p-3 rounded-lg bg-lightblue-100 text-xs text-grease-100 focus:outline-none disabled:bg-gray-200 border-none"
                                        placeholderText="YYYY-MM-DD"
                                        disabled={loading}
                                        minDate={formData.startDate ? new Date(formData.startDate) : new Date()}
                                        onFocus={(e) => e.target.setAttribute("autocomplete", "off")}
                                    />
                                </div>
                            </div>

                {/* Action Buttons */}
                            <div className="flex justify-end gap-3 pt-2">
                    <button 
                        type="button" 
                        onClick={onClose} 
                        className="px-4 py-2 text-xs text-gray-600" 
                        disabled={loading}
                    >
                                    Cancel
                                </button>
                    <button 
                        type="submit" 
                        className="px-4 py-2 bg-[#FFD700] text-white text-xs rounded-lg hover:bg-[#E6C200] transition-colors disabled:opacity-70 disabled:cursor-not-allowed" 
                        disabled={loading}
                    >
                                    {loading ? 'Submitting...' : 'Submit'}
                                </button>
                </div>
            </form>
        </AdminModal>
    );
});

AddInitialSignupModal.displayName = 'AddInitialSignupModal';

export default AddInitialSignupModal;
